import { type ResolveAsset, SceneRenderer } from "@cva/renderer";
import { elementMediaId, pageSize, type SceneDocument } from "@cva/shared";
import {
  ChevronLeft,
  ChevronRight,
  MonitorSpeaker,
  MousePointer2,
  RotateCcw,
  Square,
  X,
} from "lucide-react";
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

/**
 * Presenting a multi-page design: fullscreen slideshow with page transitions,
 * blackout, laser pointer, timer and an optional speaker view (a popup window
 * rendered through a portal, so it stays in sync with the slideshow).
 */

export type Transition = "fade" | "slide" | "zoom" | "none";

const TRANSITIONS: { value: Transition; label: string }[] = [
  { value: "fade", label: "Überblenden" },
  { value: "slide", label: "Schieben" },
  { value: "zoom", label: "Zoom" },
  { value: "none", label: "Ohne" },
];

const TRANSITION_KEY = "kaleido-present-transition";
const TRANSITION_MS = 450;
const HIDE_CONTROLS_MS = 2500;

function storedTransition(): Transition {
  try {
    const value = localStorage.getItem(TRANSITION_KEY);
    if (TRANSITIONS.some((entry) => entry.value === value)) return value as Transition;
  } catch {
    // Storage not available: default transition.
  }
  return "fade";
}

function rememberTransition(value: Transition) {
  try {
    localStorage.setItem(TRANSITION_KEY, value);
  } catch {
    // Private mode: the choice is only kept for this session.
  }
}

/** Asset ids used on one page (including grouped elements). */
export function pageAssetIds(doc: SceneDocument, pageIndex: number): string[] {
  const ids = new Set<string>();
  const visit = (id: string) => {
    const element = doc.elements[id];
    if (!element) return;
    const assetId = elementMediaId(element);
    if (assetId) ids.add(assetId);
    if (element.type === "group") for (const child of element.children) visit(child);
  };
  for (const id of doc.pages[pageIndex]?.children ?? []) visit(id);
  return [...ids];
}

function assetUrl(resolve: ResolveAsset | undefined, assetId: string): string | undefined {
  const asset = resolve?.(assetId);
  if (!asset) return undefined;
  return typeof asset === "string" ? asset : asset.url;
}

function formatTimer(ms: number): string {
  const total = Math.floor(ms / 1000);
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const seconds = total % 60;
  const mm = String(minutes).padStart(hours > 0 ? 2 : 1, "0");
  const ss = String(seconds).padStart(2, "0");
  return hours > 0 ? `${hours}:${mm}:${ss}` : `${mm}:${ss}`;
}

function useWindowSize(win: Window | null) {
  const [size, setSize] = useState(() => ({
    width: win?.innerWidth ?? 0,
    height: win?.innerHeight ?? 0,
  }));
  useEffect(() => {
    if (!win) return;
    const update = () => setSize({ width: win.innerWidth, height: win.innerHeight });
    update();
    win.addEventListener("resize", update);
    return () => win.removeEventListener("resize", update);
  }, [win]);
  return size;
}

/** One page scaled to fit a box, centred. */
function FittedPage({
  document: doc,
  pageIndex,
  resolveAsset,
  width,
  height,
}: {
  document: SceneDocument;
  pageIndex: number;
  resolveAsset: ResolveAsset | undefined;
  width: number;
  height: number;
}) {
  const size = pageSize(doc, doc.pages[pageIndex]);
  const scale = Math.min(width / size.width, height / size.height) || 0;
  return (
    <div
      style={{
        width: size.width * scale,
        height: size.height * scale,
        overflow: "hidden",
        position: "relative",
      }}
    >
      <div style={{ transform: `scale(${scale})`, transformOrigin: "0 0", pointerEvents: "none" }}>
        <SceneRenderer document={doc} pageIndex={pageIndex} resolveAsset={resolveAsset} />
      </div>
    </div>
  );
}

function keyframes(transition: Transition, direction: 1 | -1, incoming: boolean): Keyframe[] {
  switch (transition) {
    case "fade":
      return incoming ? [{ opacity: 0 }, { opacity: 1 }] : [{ opacity: 1 }, { opacity: 0 }];
    case "slide":
      return incoming
        ? [{ transform: `translateX(${direction * 100}%)` }, { transform: "translateX(0)" }]
        : [{ transform: "translateX(0)" }, { transform: `translateX(${-direction * 100}%)` }];
    case "zoom":
      return incoming
        ? [
            { opacity: 0, transform: "scale(0.85)" },
            { opacity: 1, transform: "scale(1)" },
          ]
        : [
            { opacity: 1, transform: "scale(1)" },
            { opacity: 0, transform: "scale(1.15)" },
          ];
    default:
      return [];
  }
}

export interface PresentationProps {
  document: SceneDocument;
  resolveAsset?: ResolveAsset | undefined;
  startIndex?: number;
  title: string;
  onClose: () => void;
}

export function Presentation({
  document: doc,
  resolveAsset,
  startIndex = 0,
  title,
  onClose,
}: PresentationProps) {
  const pageCount = doc.pages.length;
  const rootRef = useRef<HTMLDivElement>(null);
  const [index, setIndex] = useState(() => Math.max(0, Math.min(startIndex, pageCount - 1)));
  const [previous, setPrevious] = useState<{ index: number; direction: 1 | -1 } | null>(null);
  const [transition, setTransitionState] = useState<Transition>(storedTransition);
  const [blackout, setBlackout] = useState(false);
  const [laser, setLaser] = useState(false);
  const [pointer, setPointer] = useState<{ x: number; y: number } | null>(null);
  const [controls, setControls] = useState(true);
  const [jump, setJump] = useState("");
  const [startedAt, setStartedAt] = useState(() => Date.now());
  const [now, setNow] = useState(() => Date.now());
  const [speaker, setSpeaker] = useState<Window | null>(null);
  const size = useWindowSize(window);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  const setTransition = (value: Transition) => {
    setTransitionState(value);
    rememberTransition(value);
  };

  const indexRef = useRef(index);
  indexRef.current = index;
  const goTo = useCallback(
    (target: number) => {
      const current = indexRef.current;
      const next = Math.max(0, Math.min(pageCount - 1, target));
      setBlackout(false);
      if (next === current) return;
      indexRef.current = next;
      setPrevious({ index: current, direction: next > current ? 1 : -1 });
      setIndex(next);
    },
    [pageCount],
  );

  const close = useCallback(() => {
    if (document.fullscreenElement) void document.exitFullscreen().catch(() => undefined);
    onCloseRef.current();
  }, []);

  // Fullscreen for the whole presentation; leaving fullscreen (Esc) ends it.
  useEffect(() => {
    const root = rootRef.current;
    let entered = false;
    if (root?.requestFullscreen && document.fullscreenElement !== root) {
      root.requestFullscreen().catch(() => undefined);
    }
    const onChange = () => {
      if (document.fullscreenElement === root) entered = true;
      else if (entered && !document.fullscreenElement) onCloseRef.current();
    };
    document.addEventListener("fullscreenchange", onChange);
    return () => {
      document.removeEventListener("fullscreenchange", onChange);
      if (document.fullscreenElement) void document.exitFullscreen().catch(() => undefined);
    };
  }, []);

  // Timer tick.
  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 500);
    return () => window.clearInterval(timer);
  }, []);

  // Preload the images of the next pages.
  useEffect(() => {
    for (const target of [index + 1, index + 2]) {
      if (target >= pageCount) continue;
      for (const assetId of pageAssetIds(doc, target)) {
        const url = assetUrl(resolveAsset, assetId);
        if (url) {
          const image = new Image();
          image.decoding = "async";
          image.src = url;
        }
      }
    }
  }, [doc, index, pageCount, resolveAsset]);

  // Transition animation between the previous and the current page.
  const incomingRef = useRef<HTMLDivElement>(null);
  const outgoingRef = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    if (!previous) return;
    if (transition === "none") {
      setPrevious(null);
      return;
    }
    const options: KeyframeAnimationOptions = {
      duration: TRANSITION_MS,
      easing: "cubic-bezier(0.4, 0, 0.2, 1)",
      fill: "both",
    };
    const outgoing = outgoingRef.current?.animate(
      keyframes(transition, previous.direction, false),
      options,
    );
    const incoming = incomingRef.current?.animate(
      keyframes(transition, previous.direction, true),
      options,
    );
    const done = window.setTimeout(() => setPrevious(null), TRANSITION_MS + 20);
    return () => {
      window.clearTimeout(done);
      outgoing?.cancel();
      incoming?.cancel();
    };
  }, [previous, transition]);

  // Control bar hides after a moment without mouse movement.
  const hideTimer = useRef<number | undefined>(undefined);
  const hovering = useRef(false);
  const showControls = useCallback(() => {
    setControls(true);
    window.clearTimeout(hideTimer.current);
    hideTimer.current = window.setTimeout(() => {
      if (!hovering.current) setControls(false);
    }, HIDE_CONTROLS_MS);
  }, []);
  useEffect(() => {
    showControls();
    return () => window.clearTimeout(hideTimer.current);
  }, [showControls]);

  // Keyboard (also in the speaker window).
  const stateRef = useRef({ index, jump });
  stateRef.current = { index, jump };
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.target instanceof HTMLSelectElement) return;
      const { index: current, jump: buffer } = stateRef.current;
      const key = event.key;
      const handle = (fn: () => void) => {
        event.preventDefault();
        event.stopPropagation();
        fn();
      };
      if (event.metaKey || event.ctrlKey || event.altKey) return;
      if (/^[0-9]$/.test(key)) return handle(() => setJump((value) => (value + key).slice(-3)));
      if (key === "Enter" && buffer) {
        return handle(() => {
          goTo(Number(buffer) - 1);
          setJump("");
        });
      }
      if (key === "Backspace" && buffer) return handle(() => setJump(buffer.slice(0, -1)));
      if (buffer && key !== "Shift") setJump("");
      if (key === "ArrowRight" || key === "ArrowDown" || key === "PageDown" || key === " ") {
        return handle(() => goTo(current + 1));
      }
      if (key === "Enter") return handle(() => goTo(current + 1));
      if (key === "ArrowLeft" || key === "ArrowUp" || key === "PageUp" || key === "Backspace") {
        return handle(() => goTo(current - 1));
      }
      if (key === "Home") return handle(() => goTo(0));
      if (key === "End") return handle(() => goTo(pageCount - 1));
      if (key === "Escape") return handle(close);
      const lower = key.toLowerCase();
      if (lower === "b" || key === ".") return handle(() => setBlackout((value) => !value));
      if (lower === "l") return handle(() => setLaser((value) => !value));
    };
    // Capture: the editor's own shortcuts must not see these keys.
    window.addEventListener("keydown", onKey, true);
    speaker?.addEventListener("keydown", onKey, true);
    return () => {
      window.removeEventListener("keydown", onKey, true);
      speaker?.removeEventListener("keydown", onKey, true);
    };
  }, [goTo, close, pageCount, speaker]);

  // Speaker view: a popup window; its content is rendered through a portal.
  const [speakerRoot, setSpeakerRoot] = useState<HTMLElement | null>(null);
  const openSpeaker = () => {
    if (speaker && !speaker.closed) {
      speaker.focus();
      return;
    }
    const popup = window.open("", "kaleido-referentenansicht", "width=1100,height=700");
    if (!popup) return;
    popup.document.title = `Referentenansicht – ${title}`;
    popup.document.head.innerHTML = "";
    for (const node of document.head.querySelectorAll("style, link[rel=stylesheet]")) {
      popup.document.head.appendChild(node.cloneNode(true));
    }
    popup.document.body.innerHTML = "";
    popup.document.body.style.margin = "0";
    const container = popup.document.createElement("div");
    container.style.height = "100vh";
    popup.document.body.appendChild(container);
    setSpeaker(popup);
    setSpeakerRoot(container);
  };
  useEffect(() => {
    if (!speaker) return;
    const onUnload = () => {
      setSpeaker(null);
      setSpeakerRoot(null);
    };
    speaker.addEventListener("pagehide", onUnload);
    const poll = window.setInterval(() => {
      if (speaker.closed) onUnload();
    }, 1000);
    return () => {
      speaker.removeEventListener("pagehide", onUnload);
      window.clearInterval(poll);
    };
  }, [speaker]);
  useEffect(
    () => () => {
      if (speaker && !speaker.closed) speaker.close();
    },
    [speaker],
  );

  const elapsed = now - startedAt;
  const bar = controls || jump !== "";

  return (
    <div
      ref={rootRef}
      role="dialog"
      aria-modal="true"
      aria-label={`Präsentation: ${title}`}
      className="fixed inset-0 z-[200] select-none overflow-hidden bg-black"
      style={{ cursor: laser ? "none" : controls ? "default" : "none" }}
      onMouseMove={(event) => {
        showControls();
        if (laser) setPointer({ x: event.clientX, y: event.clientY });
      }}
      onClick={() => goTo(index + 1)}
      onContextMenu={(event) => {
        event.preventDefault();
        goTo(index - 1);
      }}
    >
      {previous ? (
        <div ref={outgoingRef} className="absolute inset-0 flex items-center justify-center">
          <FittedPage
            document={doc}
            pageIndex={previous.index}
            resolveAsset={resolveAsset}
            width={size.width}
            height={size.height}
          />
        </div>
      ) : null}
      <div
        key={index}
        ref={incomingRef}
        className="absolute inset-0 flex items-center justify-center"
      >
        <FittedPage
          document={doc}
          pageIndex={index}
          resolveAsset={resolveAsset}
          width={size.width}
          height={size.height}
        />
      </div>
      {blackout ? <div className="absolute inset-0 bg-black" /> : null}
      {laser && pointer ? (
        <div
          className="pointer-events-none absolute size-4 -translate-x-1/2 -translate-y-1/2 rounded-full bg-red-500"
          style={{
            left: pointer.x,
            top: pointer.y,
            boxShadow: "0 0 12px 4px rgba(239, 68, 68, 0.6)",
          }}
        />
      ) : null}
      {jump ? (
        <div className="absolute top-6 left-1/2 -translate-x-1/2 rounded-lg bg-black/70 px-4 py-2 text-lg text-white tabular-nums">
          Gehe zu Seite {jump} …
        </div>
      ) : null}
      <div
        role="toolbar"
        className="absolute bottom-4 left-1/2 flex -translate-x-1/2 items-center gap-1 rounded-xl bg-zinc-900/85 px-2 py-1.5 text-sm text-zinc-100 shadow-lg backdrop-blur transition-opacity duration-300"
        style={{ opacity: bar ? 1 : 0, pointerEvents: bar ? "auto" : "none" }}
        onClick={(event) => event.stopPropagation()}
        onContextMenu={(event) => event.stopPropagation()}
        onMouseEnter={() => {
          window.clearTimeout(hideTimer.current);
          hovering.current = true;
        }}
        onMouseLeave={() => {
          hovering.current = false;
        }}
      >
        <BarButton label="Zurück (←)" disabled={index === 0} onClick={() => goTo(index - 1)}>
          <ChevronLeft />
        </BarButton>
        <span className="min-w-20 text-center tabular-nums">
          {index + 1} / {pageCount}
        </span>
        <BarButton
          label="Weiter (→)"
          disabled={index >= pageCount - 1}
          onClick={() => goTo(index + 1)}
        >
          <ChevronRight />
        </BarButton>
        <span className="mx-1 h-5 w-px bg-zinc-700" />
        <span className="px-1 tabular-nums text-zinc-300" title="Dauer der Präsentation">
          {formatTimer(elapsed)}
        </span>
        <BarButton label="Timer zurücksetzen" onClick={() => setStartedAt(Date.now())}>
          <RotateCcw />
        </BarButton>
        <span className="mx-1 h-5 w-px bg-zinc-700" />
        <select
          aria-label="Seitenübergang"
          title="Seitenübergang"
          value={transition}
          onChange={(event) => setTransition(event.target.value as Transition)}
          className="h-8 rounded-md border border-zinc-700 bg-zinc-800 px-2 text-sm text-zinc-100"
        >
          {TRANSITIONS.map((entry) => (
            <option key={entry.value} value={entry.value}>
              {entry.label}
            </option>
          ))}
        </select>
        <BarButton
          label="Laserpointer (L)"
          active={laser}
          onClick={() => {
            setLaser(!laser);
          }}
        >
          <MousePointer2 />
        </BarButton>
        <BarButton label="Schwarzbild (B)" active={blackout} onClick={() => setBlackout(!blackout)}>
          <Square />
        </BarButton>
        <BarButton label="Referentenansicht" active={Boolean(speaker)} onClick={openSpeaker}>
          <MonitorSpeaker />
        </BarButton>
        <span className="mx-1 h-5 w-px bg-zinc-700" />
        <BarButton label="Beenden (Esc)" onClick={close}>
          <X />
        </BarButton>
      </div>
      {speakerRoot
        ? createPortal(
            <SpeakerView
              document={doc}
              resolveAsset={resolveAsset}
              index={index}
              elapsed={elapsed}
              blackout={blackout}
              speaker={speaker}
              onPrev={() => goTo(index - 1)}
              onNext={() => goTo(index + 1)}
              onResetTimer={() => setStartedAt(Date.now())}
            />,
            speakerRoot,
          )
        : null}
    </div>
  );
}

function BarButton({
  label,
  onClick,
  disabled,
  active,
  children,
}: {
  label: string;
  onClick: () => void;
  disabled?: boolean;
  active?: boolean;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      title={label}
      aria-label={label}
      aria-pressed={active}
      disabled={disabled}
      onClick={onClick}
      className={`inline-flex size-8 items-center justify-center rounded-md hover:bg-zinc-700 disabled:opacity-40 [&_svg]:size-4 ${
        active ? "bg-brand-600 text-white hover:bg-brand-500" : ""
      }`}
    >
      {children}
    </button>
  );
}

function SpeakerView({
  document: doc,
  resolveAsset,
  index,
  elapsed,
  blackout,
  speaker,
  onPrev,
  onNext,
  onResetTimer,
}: {
  document: SceneDocument;
  resolveAsset: ResolveAsset | undefined;
  index: number;
  elapsed: number;
  blackout: boolean;
  speaker: Window | null;
  onPrev: () => void;
  onNext: () => void;
  onResetTimer: () => void;
}) {
  const size = useWindowSize(speaker);
  const pageCount = doc.pages.length;
  const notes = doc.pages[index]?.notes?.trim();
  const mainWidth = Math.max(200, size.width * 0.58 - 48);
  const mainHeight = Math.max(150, size.height - 180);
  const sideWidth = Math.max(160, size.width * 0.42 - 48);
  return (
    <div className="flex h-full flex-col gap-4 bg-zinc-950 p-6 font-sans text-zinc-100">
      <div className="flex items-center gap-4">
        <span className="text-3xl font-semibold tabular-nums">{formatTimer(elapsed)}</span>
        <button
          type="button"
          onClick={onResetTimer}
          className="rounded-md border border-zinc-700 px-2 py-1 text-xs hover:bg-zinc-800"
        >
          Timer zurücksetzen
        </button>
        <div className="flex-1" />
        {blackout ? (
          <span className="rounded bg-zinc-800 px-2 py-1 text-xs">Schwarzbild aktiv</span>
        ) : null}
        <button
          type="button"
          onClick={onPrev}
          disabled={index === 0}
          className="rounded-md border border-zinc-700 px-3 py-1.5 text-sm hover:bg-zinc-800 disabled:opacity-40"
        >
          ‹ Zurück
        </button>
        <span className="min-w-24 text-center text-lg tabular-nums">
          Seite {index + 1} / {pageCount}
        </span>
        <button
          type="button"
          onClick={onNext}
          disabled={index >= pageCount - 1}
          className="rounded-md border border-zinc-700 px-3 py-1.5 text-sm hover:bg-zinc-800 disabled:opacity-40"
        >
          Weiter ›
        </button>
      </div>
      <div className="flex min-h-0 flex-1 gap-6">
        <div className="flex flex-col gap-2">
          <p className="text-xs font-medium text-zinc-400">Aktuelle Seite</p>
          <div className="shadow-lg ring-1 ring-zinc-800">
            <FittedPage
              document={doc}
              pageIndex={index}
              resolveAsset={resolveAsset}
              width={mainWidth}
              height={mainHeight}
            />
          </div>
        </div>
        <div className="flex min-w-0 flex-1 flex-col gap-4">
          <div className="flex flex-col gap-2">
            <p className="text-xs font-medium text-zinc-400">Nächste Seite</p>
            {index + 1 < pageCount ? (
              <div className="self-start opacity-90 ring-1 ring-zinc-800">
                <FittedPage
                  document={doc}
                  pageIndex={index + 1}
                  resolveAsset={resolveAsset}
                  width={sideWidth}
                  height={mainHeight * 0.4}
                />
              </div>
            ) : (
              <p className="text-sm text-zinc-500">Ende der Präsentation</p>
            )}
          </div>
          <div className="flex min-h-0 flex-1 flex-col gap-2">
            <p className="text-xs font-medium text-zinc-400">Notizen</p>
            <div className="min-h-0 flex-1 overflow-y-auto rounded-lg bg-zinc-900 p-4 text-lg leading-relaxed whitespace-pre-wrap">
              {notes || <span className="text-zinc-500">Keine Notizen für diese Seite.</span>}
            </div>
          </div>
        </div>
      </div>
      <p className="text-xs text-zinc-500">
        → / Leertaste weiter · ← zurück · Zahl + Enter springen · B Schwarzbild · L Laserpointer ·
        Esc beenden
      </p>
    </div>
  );
}
